// React
import { ReactElement, useEffect, useState } from 'react';
// Styles
import styles from './bus-reservation.module.scss';
import { DashboardMemberInformationView } from '@utmb/types/index';
import { useTranslation } from 'next-i18next';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { Field, FieldArray, ErrorMessage, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useUser } from '@utmb/contexts/AuthContext';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { Button } from '@utmb/design-system/button/Button';
import { Font } from '@utmb/design-system/text/font';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { useRouter } from 'next/router';
import * as React from 'react';
import DatepickerField from '@utmb/features/bus-reservation/DatepickerField';
import CheckoutForm from '@utmb/features/bus-reservation/CheckoutForm';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js/types/stripe-js/elements-group';
import ShuttleLineView from '@utmb/types/view/ShuttleLineView';
import SelectableBusPassView from '@utmb/types/view/SelectableBusPassView';
import BookingBusPassPersonDto from '@utmb/types/dto/BookingBusPassPersonDto';
import { PassReservationPriceTypeEnum } from '@utmb/types/enum/PassReservationPriceTypeEnum';
import BookingBusPassDto from '@utmb/types/dto/BookingBusPassDto';
import BookingBusPassShuttleDto from '@utmb/types/dto/BookingBusPassShuttleDto';
import { convertLocaleTimezoneDateToUtc, simpleDateToIsoString } from '@utmb/utils/date';
import { toast } from 'react-toastify';
import { PaymentIntent } from '@stripe/stripe-js';
import { InputTel } from '@utmb/design-system/input-tel/InputTel';
import { Select } from '@utmb/design-system/select-new/Select';
import BusParametersDataView from '@utmb/types/view/BusParametersDataView';

import { DetailPaymentFullBus, getBusAmountDetail } from '@utmb/utils/bus.utils';

interface BusReservationFormProps {
    id?: string;
    busParams: BusParametersDataView;
}

export const BusReservationForm = (props: BusReservationFormProps): ReactElement => {
    const { id, busParams } = props;
    const { t } = useTranslation();
    const { tenantId } = useTenancy();
    const { locale } = useRouter();

    const [isLoading, setIsLoading] = useState(false);
    const [busInformation, setBusInformation] = useState<SelectableBusPassView[] | undefined>();
    const [shuttleLines, setShuttleLines] = useState<ShuttleLineView[] | undefined>();
    const [shuttleLinesTab, setShuttleLinesTab] = useState<string[]>();

    const [selectedBusInformation, setSelectedBusInformation] = useState<SelectableBusPassView[] | undefined>();

    const [reducedAge, setReducedAge] = useState<number>(0);

    const [phoneErrorMessage, setPhoneErrorMessage] = useState('');

    const [planSelected, setPlanSelected] = useState<number[] | undefined>();
    const [reducedYear, setReducedYear] = useState<number | undefined>();

    const [amountBusDetail, setAmountBusDetail] = useState<DetailPaymentFullBus | null>();

    const [errorMessageQuantity, setErrorMessageQuantity] = useState(false);
    // const [errorMessageShuttles, _setErrorMessageShuttles] = useState(false);

    const [canPay, setCanPay] = useState(true);

    const [clientSecret, setClientSecret] = useState('');
    const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);

    let callbackUrl = '';
    if (typeof window !== 'undefined') {
        callbackUrl = window.location.href;
        callbackUrl = callbackUrl.split('?')[0];
    }

    const options: StripeElementsOptions = {
        clientSecret,
    };

    const personInit = {
        firstname: '',
        lastname: '',
        birthdate: '',
    };

    async function getBusInformation(): Promise<void> {
        setIsLoading(true);

        const myBusInformation: SelectableBusPassView[] = (await fetchTheAPI({
            tenantId,
            isClient: true,
            path: `/bus`,
            params: { lang: locale as string },
        })) as SelectableBusPassView[];

        setBusInformation(myBusInformation);

        const selectedBusData = myBusInformation?.filter((pass) => selectedBusInformation?.map((info) => info.id).includes(pass.id));
        setSelectedBusInformation(selectedBusData);
        setReducedAge(Math.min(...selectedBusData.map((data) => data.reducedAge)));
        setIsLoading(false);
    }

    useEffect(() => {
        getBusInformation().then();
    }, []);

    const { dashboardMember } = useDashboard();
    const { me } = useUser();

    const { firstName, lastName } = dashboardMember?.information || ({} as DashboardMemberInformationView);
    const email = me?.email ? me.email : '';

    const initialValues: {
        firstname: string;
        lastname: string;
        email: string;
        emailConfirmation: string;
        plan: string;
        phone: string;
        personsAdults: {
            firstname: string;
            lastname: string;
            birthdate: string;
        }[];
        personsChildren: {
            firstname: string;
            lastname: string;
            birthdate: string;
        }[];
        shuttleLines?: string[];
    } = {
        firstname: firstName ?? '',
        lastname: lastName ?? '',
        email: email ?? '',
        emailConfirmation: email ?? '',
        plan: '',
        phone: '',
        personsAdults: [personInit],
        personsChildren: [],
        shuttleLines: shuttleLinesTab,
    };

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object({
            firstname: Yup.string().required(t('busReservation.firstNameRequired')),
            lastname: Yup.string().required(t('busReservation.lastNameRequired')),
            email: Yup.string().required(t('busReservation.emailRequired')).email(t('busReservation.wrongEmail')),
            emailConfirmation: Yup.string()
                .required(t('busReservation.emailRequired'))
                .oneOf([Yup.ref('email'), undefined], t('busReservation.emailMustMatch')),
            phone: Yup.string()
                .max(30, t('form.lengthExceededError', { length: 30 }))
                .required(t('busReservation.contactPhoneRequired')),
            personsAdults: Yup.array().of(
                Yup.object().shape({
                    firstname: Yup.string().required(t('busReservation.firstNameRequired')),
                    lastname: Yup.string().required(t('busReservation.lastNameRequired')),
                    birthdate: Yup.string().required(t('busReservation.birthdateRequired')),
                })
            ),
            personsChildren: Yup.array().of(
                Yup.object().shape({
                    firstname: Yup.string().required(t('busReservation.firstNameRequired')),
                    lastname: Yup.string().required(t('busReservation.lastNameRequired')),
                    birthdate: Yup.string().required(t('busReservation.birthdateRequired')),
                })
            ),
        }),
        enableReinitialize: true,
        onSubmit: () => handlePayment(),
    });

    const { values, dirty, isValid } = formik;

    const handlePlanSelect = (options: { label: string; value: string | number }[] | { label: string; value: string | number }) => {
        const values = Array.isArray(options) ? options.map((option) => +option.value) : [+options.value];
        setPlanSelected(values);

        const selectedBusData = busInformation?.filter((pass) => values.includes(pass.id));
        setSelectedBusInformation(selectedBusData);

        setReducedAge(Math.min(...(selectedBusData?.map((data) => data.reducedAge) || [0])));
    };

    useEffect(() => {
        handleShuttles();
    }, [selectedBusInformation]);

    const handleShuttles = () => {
        setShuttleLines(Array.from(new Map(selectedBusInformation?.flatMap((info) => info.shuttleLines)?.map((line) => [line.id, line]) || []).values()));

        const year = new Date().getFullYear();

        if (reducedAge !== 0) {
            setReducedYear(year - reducedAge);
        }
    };

    useEffect(() => {
        const tmpArray: string[] = [];
        shuttleLines?.map((shuttleLine: ShuttleLineView) => {
            const value = `${shuttleLine.id},0,100`;
            tmpArray.push(value);
        });
        setShuttleLinesTab(tmpArray);
    }, [shuttleLines]);

    const handlePayment = () => {
        if (canPay) {
            setIsLoading(true);
            const personsArray: BookingBusPassPersonDto[] = [];
            values?.personsAdults.map((adult: { firstname: string; lastname: string; birthdate: string }) => {
                if (adult.firstname && adult.lastname && adult.birthdate) {
                    const personData: BookingBusPassPersonDto = {
                        firstname: adult.firstname,
                        lastname: adult.lastname,
                        birthdate: simpleDateToIsoString(convertLocaleTimezoneDateToUtc(adult.birthdate)),
                        priceType: PassReservationPriceTypeEnum.FULL,
                    };
                    personsArray.push(personData);
                }
            });
            values?.personsChildren?.map((child: { firstname: string; lastname: string; birthdate: string }) => {
                if (child.firstname && child.lastname && child.birthdate) {
                    const personData: BookingBusPassPersonDto = {
                        firstname: child.firstname,
                        lastname: child.lastname,
                        birthdate: simpleDateToIsoString(convertLocaleTimezoneDateToUtc(child.birthdate)),
                        priceType: PassReservationPriceTypeEnum.REDUCED,
                    };
                    personsArray.push(personData);
                }
            });

            const shuttlesArray: BookingBusPassShuttleDto[] = [];
            values?.shuttleLines?.map((shuttle: string) => {
                if (shuttle !== undefined) {
                    const shuttleTmp = shuttle.split(',');
                    const shuttleData: BookingBusPassShuttleDto = { shuttleLineId: Number(shuttleTmp[0]), shuttleId: Number(shuttleTmp[1]) };
                    shuttlesArray.push(shuttleData);
                }
            });

            const body: BookingBusPassDto = {
                busPassIds: planSelected ?? [],
                firstname: values.firstname,
                lastname: values.lastname,
                email: values.email,
                nbFull: values.personsAdults.length,
                nbReduced: values.personsChildren.length,
                persons: personsArray,
                phone: values.phone,
                shuttles: shuttlesArray,
                callbackUrl,
                locale: locale ?? 'en',
            };

            fetchTheAPI({
                isClient: true,
                tenantId,
                path: `/bus/book`,
                method: FetchMethodsEnum.POST,
                body: { ...body },
            })
                .then((result: any) => {
                    if (result === null) {
                        toast.error('An unknown error occured');
                    } else if (result.statusCode === 400) {
                        toast.error(`${result.message}`);
                    } else if (result.statusCode === 404) {
                        toast.error(`BusPass not found`);
                    } else if (result.statusCode === 409) {
                        toast.error(t('busReservation.quotaMax'));
                        getBusInformation().then();
                    } else {
                        setClientSecret(result.client_secret);
                        setPaymentIntent(result);
                    }
                })
                .catch((error) => {
                    toast.error(error);
                });

            setIsLoading(false);
        }
    };

    const handleQuantity = () => {
        setCanPay(true);
        setErrorMessageQuantity(false);

        if (
            selectedBusInformation &&
            values.personsAdults.length + values.personsChildren.length > Math.min(...selectedBusInformation.map((data) => data.available))
        ) {
            setCanPay(false);
            setErrorMessageQuantity(true);
        } else {
            values?.shuttleLines?.map((shuttle: string) => {
                const shuttleTmp = shuttle.split(',');
                if (values.personsAdults.length + values.personsChildren.length > Number(shuttleTmp[2])) {
                    setCanPay(false);
                    setErrorMessageQuantity(true);
                }
            });
        }

        // const nbA = values.personsAdults.length;
        // const busPass = selectedBusInformation;
        // const definedMaxNbA = busPass?.priceFor5 ? 5 : busPass?.priceFor4 ? 4 : busPass?.priceFor3 ? 3 : busPass?.priceFor2 ? 2 : 1;
        // const definedMinPricePerAdult = busPass?.priceFor5
        //     ? busPass.priceFor5 / 5
        //     : busPass?.priceFor4
        //     ? busPass.priceFor4 / 4
        //     : busPass?.priceFor3
        //     ? busPass.priceFor3 / 3
        //     : busPass?.priceFor2
        //     ? busPass.priceFor2 / 2
        //     : busPass?.price ?? 0;
        // if (nbA >= definedMaxNbA) setTotalAdults(Math.round(100 * nbA * definedMinPricePerAdult) / 100);
        // else if (nbA === 4 && busPass?.priceFor4) setTotalAdults(Math.round(100 * busPass.priceFor4) / 100);
        // else if (nbA === 3 && busPass?.priceFor3) setTotalAdults(Math.round(100 * busPass.priceFor3) / 100);
        // else if (nbA === 2 && busPass?.priceFor2) setTotalAdults(Math.round(100 * busPass.priceFor2) / 100);
        // else setTotalAdults(Math.round(100 * nbA * (busPass?.price ?? 0)) / 100);

        const nbA = values.personsAdults.length;
        const nbR = values.personsChildren.length;

        if (!selectedBusInformation) return;

        const tempAmountDetail = getBusAmountDetail(selectedBusInformation, busParams, nbA, nbR);
        if (tempAmountDetail) setAmountBusDetail(tempAmountDetail);
        else setAmountBusDetail(null);
    };

    useEffect(() => {
        handleQuantity();
    }, [values, busInformation, selectedBusInformation]);

    const publicKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ? process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY : '';
    const stripePromise = loadStripe(publicKey);

    return (
        <>
            {busInformation ? (
                <section id={id} className={styles.shuttleBooking}>
                    <Font.OxaniumMedium desktop="16" mobile="16" className={styles.information}>
                        {t('busReservation.information')}
                    </Font.OxaniumMedium>
                    <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit} className={[styles.form, clientSecret ? styles.isSubmitting : null].filter(Boolean).join(' ')}>
                            <div className={styles.legend}>
                                <Font.OxaniumBold as="span" mobile="14" desktop="14" color={ColorTypesEnum.WHITE}>
                                    {t('busReservation.plan')}
                                </Font.OxaniumBold>
                            </div>
                            <div className={styles.formRow}>
                                <div className={styles.formItem}>
                                    <div className={styles.input}>
                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                            <span dangerouslySetInnerHTML={{ __html: `${t('busReservation.planDetail')} *` }} />
                                        </Font.OxaniumRegular>

                                        <Select
                                            id="plan"
                                            name="plan"
                                            placeholder={t('busReservation.selectPlan')}
                                            value={(() => {
                                                const selectedPlan = busInformation.filter((target) => planSelected?.includes(target.id));
                                                return selectedPlan
                                                    ? selectedPlan.map((plan) => ({
                                                          label: `${plan.name} (${t('busReservation.availablePass', {
                                                              count: plan.available,
                                                              ordinal: true,
                                                          })})`,
                                                          value: plan.id,
                                                      }))
                                                    : null;
                                            })()}
                                            onChange={handlePlanSelect}
                                            options={busInformation.map((data) => ({
                                                label: `${data.name} (${t('busReservation.availablePass', { count: data.available, ordinal: true })})`,
                                                value: data.id,
                                            }))}
                                            isMulti={busParams.isMultiplePass}
                                            closeMenuOnSelect={!busParams.isMultiplePass}
                                        />

                                        <div className={styles.errorMessage}>
                                            <ErrorMessage name={'plan'} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.legend}>
                                <Font.OxaniumBold as="span" mobile="14" desktop="14" color={ColorTypesEnum.WHITE}>
                                    {t('busReservation.generalInformation')}
                                </Font.OxaniumBold>
                            </div>
                            <div className={styles.formRow}>
                                <div className={styles.formItem}>
                                    <div className={styles.input}>
                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                            <label>{t('busReservation.firstName')} *</label>
                                        </Font.OxaniumRegular>
                                        <Field name="firstname" type={'text'} />
                                        <div className={styles.errorMessage}>
                                            <ErrorMessage name={'firstname'} />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.formItem}>
                                    <div className={styles.input}>
                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                            <label>{t('busReservation.lastName')} *</label>
                                        </Font.OxaniumRegular>
                                        <Field name="lastname" type={'text'} />
                                        <div className={styles.errorMessage}>
                                            <ErrorMessage name={'lastname'} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.formRow}>
                                <div className={styles.formItem}>
                                    <div className={styles.input}>
                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                            <label>{t('busReservation.email')} *</label>
                                        </Font.OxaniumRegular>
                                        <Field name="email" type={'text'} />
                                        <div className={styles.errorMessage}>
                                            <ErrorMessage name={'email'} />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.formItem}>
                                    <div className={styles.input}>
                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                            <label>{t('busReservation.emailConfirmation')} *</label>
                                        </Font.OxaniumRegular>
                                        <Field name="emailConfirmation" type={'text'} />
                                        <div className={styles.errorMessage}>
                                            <ErrorMessage name={'emailConfirmation'} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {planSelected ? (
                                <>
                                    <div className={styles.legend}>
                                        <Font.OxaniumBold as="span" mobile="14" desktop="14" color={ColorTypesEnum.WHITE}>
                                            {reducedAge ? t('busReservation.adults', { minAge: reducedAge + 1 }) : t('busReservation.persons')}
                                        </Font.OxaniumBold>
                                    </div>

                                    <div className={styles.formRow}>
                                        <div className={styles.formItem}>
                                            <div>
                                                <InputTel
                                                    id="phone"
                                                    name="phone"
                                                    label={t('busReservation.contactPhone')}
                                                    value={formik.values.phone}
                                                    onChange={(fullNumber: string, value: string, isValid: boolean) => {
                                                        formik.setFieldValue('phone', fullNumber);
                                                        setPhoneErrorMessage(!isValid ? t('form.telNotValidError') : '');
                                                    }}
                                                    onBlur={() => {
                                                        formik.setFieldTouched('phone', true);
                                                    }}
                                                    required={true}
                                                />
                                                <div className={styles.errorMessage}>
                                                    {phoneErrorMessage ? (
                                                        <>
                                                            {phoneErrorMessage}
                                                            <br />
                                                        </>
                                                    ) : null}
                                                    <ErrorMessage name={'phone'} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <FieldArray
                                        name="personsAdults"
                                        render={(arrayHelpers) => {
                                            const personsAdults = values.personsAdults;
                                            return (
                                                <div className={styles.fieldArray}>
                                                    {personsAdults && personsAdults.length > 0
                                                        ? personsAdults.map((user, index) => (
                                                              <div key={`adult${index}`}>
                                                                  <div className={styles.formRow}>
                                                                      <div className={styles.formItem}>
                                                                          <div className={styles.input}>
                                                                              <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                                                  <label>{t('busReservation.firstName')} *</label>
                                                                              </Font.OxaniumRegular>
                                                                              <Field placeholder="" name={`personsAdults[${index}].firstname`} />
                                                                              <div className={styles.errorMessage}>
                                                                                  <ErrorMessage name={`personsAdults[${index}].firstname`} />
                                                                              </div>
                                                                          </div>
                                                                      </div>
                                                                      <div className={styles.formItem}>
                                                                          <div className={styles.input}>
                                                                              <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                                                  <label>{t('busReservation.lastName')} *</label>
                                                                              </Font.OxaniumRegular>
                                                                              <Field placeholder="" name={`personsAdults[${index}].lastname`} />
                                                                              <div className={styles.errorMessage}>
                                                                                  <ErrorMessage name={`personsAdults[${index}].lastname`} />
                                                                              </div>
                                                                          </div>
                                                                      </div>
                                                                      <div className={styles.formItem}>
                                                                          <div className={styles.input}>
                                                                              <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                                                  <label>{t('busReservation.birthdate')} *</label>
                                                                              </Font.OxaniumRegular>
                                                                              <Field
                                                                                  name={`personsAdults[${index}].birthdate`}
                                                                                  component={DatepickerField}
                                                                                  maxYear={reducedYear ? reducedYear - 1 : undefined}
                                                                                  minYear={1900}
                                                                              />
                                                                              <div className={styles.errorMessage}>
                                                                                  <ErrorMessage name={`personsAdults[${index}].birthdate`} />
                                                                              </div>
                                                                          </div>
                                                                      </div>

                                                                      <div className={styles.formItem}>
                                                                          {index > 0 ? (
                                                                              <Button.Default
                                                                                  color={getColorStyle(ColorTypesEnum.WHITE)}
                                                                                  bgcolor={getColorStyle(ColorTypesEnum.DEFAULT)}
                                                                                  type="button"
                                                                                  onPress={() => arrayHelpers.remove(index)}
                                                                                  label="X"
                                                                                  className={styles.button}
                                                                              />
                                                                          ) : (
                                                                              <></>
                                                                          )}
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                          ))
                                                        : null}
                                                    <Button.Default
                                                        color={getColorStyle(ColorTypesEnum.SECONDARY)}
                                                        bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                                                        type="button"
                                                        onPress={() => {
                                                            if (busParams.limitAdults && personsAdults.length >= busParams.limitAdults)
                                                                toast.error(t('busReservation.adultMaxError', { nb: busParams.limitAdults }));
                                                            else arrayHelpers.push(personInit);
                                                        }}
                                                        label={t('busReservation.add')}
                                                        className={styles.button}
                                                    />
                                                </div>
                                            );
                                        }}
                                    />
                                    {reducedAge ? (
                                        <>
                                            <div className={styles.legend}>
                                                <Font.OxaniumBold as="span" mobile="14" desktop="14" color={ColorTypesEnum.WHITE}>
                                                    {t('busReservation.children', { maxAge: reducedAge })}
                                                </Font.OxaniumBold>
                                            </div>

                                            <FieldArray
                                                name="personsChildren"
                                                render={(arrayHelpers) => {
                                                    const personsChildren = values.personsChildren;
                                                    return (
                                                        <div className={styles.fieldArray}>
                                                            {personsChildren && personsChildren.length > 0
                                                                ? personsChildren.map((child, index) => (
                                                                      <div key={`children${index}`}>
                                                                          <div className={styles.formRow}>
                                                                              <div className={styles.formItem}>
                                                                                  <div className={styles.input}>
                                                                                      <Font.OxaniumRegular
                                                                                          as="span"
                                                                                          mobile="12"
                                                                                          desktop="12"
                                                                                          className={styles.label}
                                                                                      >
                                                                                          <label>{t('busReservation.firstName')} *</label>
                                                                                      </Font.OxaniumRegular>
                                                                                      <Field placeholder="" name={`personsChildren.${index}.firstname`} />
                                                                                      <div className={styles.errorMessage}>
                                                                                          <ErrorMessage name={`personsChildren.${index}.firstname`} />
                                                                                      </div>
                                                                                  </div>
                                                                              </div>
                                                                              <div className={styles.formItem}>
                                                                                  <div className={styles.input}>
                                                                                      <Font.OxaniumRegular
                                                                                          as="span"
                                                                                          mobile="12"
                                                                                          desktop="12"
                                                                                          className={styles.label}
                                                                                      >
                                                                                          <label>{t('busReservation.lastName')} *</label>
                                                                                      </Font.OxaniumRegular>
                                                                                      <Field name={`personsChildren.${index}.lastname`} />
                                                                                      <div className={styles.errorMessage}>
                                                                                          <ErrorMessage name={`personsChildren.${index}.lastname`} />
                                                                                      </div>
                                                                                  </div>
                                                                              </div>
                                                                              <div className={styles.formItem}>
                                                                                  <div className={styles.input}>
                                                                                      <Font.OxaniumRegular
                                                                                          as="span"
                                                                                          mobile="12"
                                                                                          desktop="12"
                                                                                          className={styles.label}
                                                                                      >
                                                                                          <label>{t('busReservation.birthdate')} *</label>
                                                                                      </Font.OxaniumRegular>
                                                                                      <Field
                                                                                          name={`personsChildren.${index}.birthdate`}
                                                                                          component={DatepickerField}
                                                                                          minYear={reducedYear ?? undefined}
                                                                                          maxYear={new Date().getFullYear()}
                                                                                      />
                                                                                      <div className={styles.errorMessage}>
                                                                                          <ErrorMessage name={`personsChildren.${index}.birthdate`} />
                                                                                      </div>
                                                                                  </div>
                                                                              </div>

                                                                              <div className={styles.formItem}>
                                                                                  <Button.Default
                                                                                      color={getColorStyle(ColorTypesEnum.WHITE)}
                                                                                      bgcolor={getColorStyle(ColorTypesEnum.DEFAULT)}
                                                                                      type="button"
                                                                                      onPress={() => arrayHelpers.remove(index)}
                                                                                      label="X"
                                                                                      className={styles.button}
                                                                                  />
                                                                              </div>
                                                                          </div>
                                                                      </div>
                                                                  ))
                                                                : null}
                                                            <Button.Default
                                                                color={getColorStyle(ColorTypesEnum.SECONDARY)}
                                                                bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                                                                type="button"
                                                                onPress={() => arrayHelpers.push(personInit)}
                                                                label={t('busReservation.add')}
                                                                className={styles.button}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            />
                                        </>
                                    ) : null}
                                    {!isLoading && shuttleLines && shuttleLines.length > 0 ? (
                                        <div className={styles.legend}>
                                            <Font.OxaniumBold as="span" mobile="14" desktop="16" color={ColorTypesEnum.WHITE}>
                                                {t('busReservation.shuttles')}
                                            </Font.OxaniumBold>
                                            <br />
                                            <Font.OxaniumBold as="span" mobile="12" desktop="14" color={ColorTypesEnum.WHITE}>
                                                {t('busReservation.shuttlesDetails')}
                                            </Font.OxaniumBold>
                                        </div>
                                    ) : null}
                                    {!isLoading &&
                                        shuttleLines &&
                                        shuttleLines.length > 0 &&
                                        shuttleLines?.map((shuttleLine, index) => (
                                            <div key={index} className={styles.formRow}>
                                                <div className={styles.formItem}>
                                                    <div className={styles.input}>
                                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                            {t(`busReservation.shuttleLine.${shuttleLine.type}`)}
                                                        </Font.OxaniumRegular>
                                                        <Font.OxaniumRegular as="span" mobile="12" desktop="14">
                                                            {shuttleLine.name}
                                                        </Font.OxaniumRegular>
                                                    </div>
                                                </div>
                                                <div className={styles.formItem}>
                                                    <div className={styles.input}>
                                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                            <label>{t('busReservation.shuttle')} *</label>
                                                        </Font.OxaniumRegular>
                                                        <Field as="select" name={`shuttleLines.${index}`}>
                                                            <option value={shuttleLine.id + ',0,100'}>{t('busReservation.empty')}</option>
                                                            {shuttleLine.shuttles.map((shuttle, index) => (
                                                                <option
                                                                    key={index}
                                                                    value={shuttleLine.id + ',' + shuttle.id + ',' + shuttle.available}
                                                                    disabled={
                                                                        shuttle.available <
                                                                        (values.personsAdults?.length ?? 0) + (values.personsChildren?.length ?? 0)
                                                                    }
                                                                >
                                                                    {shuttle.name} {shuttle.schedule} (
                                                                    {t('busReservation.availableShuttles', { count: shuttle.available, ordinal: true })})
                                                                </option>
                                                            ))}
                                                        </Field>
                                                        <div className={styles.errorMessage}>
                                                            <ErrorMessage name={'shuttleLine'} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                    <div className={styles.legend}>
                                        <Font.OxaniumBold as="span" mobile="14" desktop="14" color={ColorTypesEnum.WHITE}>
                                            {t('busReservation.payment')}
                                        </Font.OxaniumBold>
                                    </div>
                                    <div className={[styles.formRow, styles.summaryPrice].join(' ')}>
                                        <div className={styles.formItem}>
                                            <div className={styles.formResults}>
                                                {amountBusDetail ? (
                                                    <>
                                                        {amountBusDetail?.passDetails.map((pass) => (
                                                            <div key={pass.busPassId}>
                                                                <div className={styles.row}>
                                                                    <Font.OxaniumBold as="div" mobile="14" desktop="14">
                                                                        {selectedBusInformation?.find((info) => info.id === pass.busPassId)?.name}
                                                                    </Font.OxaniumBold>
                                                                </div>
                                                                <div className={styles.row}>
                                                                    <Font.OxaniumMedium as="div" mobile="14" desktop="14">
                                                                        {t('busReservation.normalPrice')}
                                                                    </Font.OxaniumMedium>
                                                                    <Font.OxaniumBold as="div" mobile="14" desktop="14">
                                                                        (x{values.personsAdults.length}) = {pass.adultPrice} {busParams.currency}
                                                                    </Font.OxaniumBold>
                                                                    {pass.adultPrice !== pass.adultTheoricalPrice ? (
                                                                        <>
                                                                            <Font.OxaniumMedium
                                                                                as="div"
                                                                                mobile="14"
                                                                                desktop="14"
                                                                                className={styles.lineThroughText}
                                                                            >
                                                                                {pass.adultTheoricalPrice} {busParams.currency}
                                                                            </Font.OxaniumMedium>
                                                                            <Font.OxaniumMedium as="div" mobile="14" desktop="14">
                                                                                (Remise de groupe)
                                                                            </Font.OxaniumMedium>
                                                                        </>
                                                                    ) : null}
                                                                </div>
                                                                {reducedAge && values.personsChildren.length > 0 ? (
                                                                    <div className={styles.row}>
                                                                        <Font.OxaniumMedium as="div" mobile="14" desktop="14">
                                                                            {t('busReservation.reducedPrice')}
                                                                        </Font.OxaniumMedium>
                                                                        <Font.OxaniumBold as="div" mobile="14" desktop="14">
                                                                            (x{values.personsChildren.length}) = {pass.childrenPrice} {busParams.currency}
                                                                        </Font.OxaniumBold>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        ))}

                                                        <div>
                                                            {amountBusDetail.theoricalFinalPrice !== amountBusDetail.finalPrice ? (
                                                                <>
                                                                    <Font.OxaniumBold as="div" mobile="14" desktop="14">
                                                                        {t('busReservation.totalPrice')}
                                                                    </Font.OxaniumBold>

                                                                    <div>
                                                                        <div className={styles.row}>
                                                                            <Font.OxaniumMedium as="div" mobile="14" desktop="14">
                                                                                {t('busReservation.total')}
                                                                            </Font.OxaniumMedium>
                                                                            <Font.OxaniumBold as="div" mobile="14" desktop="14">
                                                                                {amountBusDetail.theoricalFinalPrice} {busParams.currency}
                                                                            </Font.OxaniumBold>
                                                                        </div>
                                                                        <div className={styles.row}>
                                                                            <Font.OxaniumMedium as="div" mobile="14" desktop="14">
                                                                                {t('busReservation.discountMultiPass')}
                                                                            </Font.OxaniumMedium>
                                                                            <Font.OxaniumBold as="div" mobile="14" desktop="14">
                                                                                {amountBusDetail.discountPercent}%
                                                                            </Font.OxaniumBold>
                                                                        </div>
                                                                        <div className={`${styles.row} ${styles.totalToPay}`}>
                                                                            <Font.OxaniumBold as="div" mobile="16" desktop="16">
                                                                                {t('busReservation.totalToPay')}
                                                                            </Font.OxaniumBold>
                                                                            <Font.OxaniumBold as="div" mobile="16" desktop="16">
                                                                                {amountBusDetail.finalPrice} {busParams.currency}
                                                                            </Font.OxaniumBold>
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className={styles.row}>
                                                                        <Font.OxaniumBold as="div" mobile="16" desktop="16">
                                                                            {t('busReservation.totalPrice')}
                                                                        </Font.OxaniumBold>
                                                                        <Font.OxaniumBold as="div" mobile="16" desktop="16">
                                                                            {amountBusDetail.finalPrice} {busParams.currency}
                                                                        </Font.OxaniumBold>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>

                                            {errorMessageQuantity ? (
                                                <Font.OxaniumMedium
                                                    as={'div'}
                                                    desktop="16"
                                                    mobile="16"
                                                    className={styles.reservationNotFound}
                                                    color={getColorStyle('red')}
                                                >
                                                    {t('busReservation.quotaMax')}
                                                </Font.OxaniumMedium>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.formRow}>
                                        <div className={styles.formItem}>
                                            <Button.Default
                                                color={getColorStyle(ColorTypesEnum.SECONDARY)}
                                                bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                                                type="submit"
                                                label={t('busReservation.confirmAndPay')}
                                                className={styles.button}
                                                disabled={!canPay || errorMessageQuantity || !dirty || !isValid}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                        </form>
                    </FormikProvider>
                    {clientSecret && (
                        <div className={styles.form}>
                            <div className={styles.legend}>
                                <Font.OxaniumBold as="span" mobile="14" desktop="14" color={ColorTypesEnum.WHITE}>
                                    {t('busReservation.payment')} ({(paymentIntent?.amount ?? 0) / 100} {paymentIntent?.currency})
                                </Font.OxaniumBold>
                            </div>
                            <Elements options={options} stripe={stripePromise}>
                                <CheckoutForm />
                            </Elements>
                        </div>
                    )}
                </section>
            ) : (
                <Font.OxaniumMedium desktop="16" mobile="16" className={styles.information}>
                    {t('busReservation.closed')}
                </Font.OxaniumMedium>
            )}
        </>
    );
};
